import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./AppCustomProperties.css";
import "../../../ds2/components/Table.css";
import { Button } from "../../../ds2/components";
import { AppCustomPropertyNew } from "./AppCustomPropertyNew";
import { CustomProperty, RoleType, EmployeeType } from "winslow-core";
import { apiGetCustomPropertiesForWorkspace, apiGetCustomPropertiesNotVibeForWorkspace } from "../../../services/EmployeeRecordService";
import { useDispatch, useSelector } from "react-redux";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import toast from "react-hot-toast";
import { SETTINGS_COMPONENTS } from "./AppCustomPropertyNew";
import { SETTINGS_DISPLAY, ROLE_DISPLAY, EMPLOYEE_TYPE_DISPLAY } from "./customPropertyConfig";
import { Shield, Users, User, Check, X } from "lucide-react";
import { MyTooltip } from "../../common/MyTooltip";
import { GenericSelect } from "../../shared/GenericSelect";

// Role icon mapping
const ROLE_ICONS = {
    [RoleType.ADMIN]: <Shield size={16} className="role-icon" />,
    [RoleType.MANAGER]: <Users size={16} className="role-icon" />,
    [RoleType.EMPLOYEE]: <User size={16} className="role-icon" />,
};

type AppCustomPropertiesProps = {

}   

export const AppCustomProperties = ({} : AppCustomPropertiesProps) => {

    const storeUser = useSelector(selectUser);
    const uiStates = useSelector(selectUIStates);
    
    const { customPropertyId } = useParams();   // id or "new"
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [customProperties, setCustomProperties] = useState<CustomProperty[] | undefined>(undefined);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [selectedEmployeeType, setSelectedEmployeeType] = useState<string>("All");

    useEffect(() => {
        dispatch(setUIStates({...uiStates, hrisLocation: "Customize"}));
    }, []);

    useEffect(() => {
        // if path is /app/custom-properties/"", then fetch custom properties
        if (!customProperties || customPropertyId === "refresh") {
            (async () => {
                // alert("fetching custom properties");
                const cps = await apiGetCustomPropertiesNotVibeForWorkspace(storeUser?.currentWorkspaceId || "");

                if (!cps) {
                    toast("Error loading custom properties");
                    return;
                }

                setCustomProperties(cps);
            })();
        }
    },[customPropertyId]);

    // Filter and sort custom properties based on search term and employee type
    const filteredCustomProperties = customProperties?.filter((cp) => {
        // Exclude vibe properties
        if (cp.isVibe) {
            return false;
        }
        
        // Search term filter
        const matchesSearch = !searchTerm || 
            cp.display.toLowerCase().includes(searchTerm.toLowerCase()) ||
            cp.label.toLowerCase().includes(searchTerm.toLowerCase());
        
        // Employee type filter
        const matchesEmployeeType = selectedEmployeeType === "All" || 
            cp.visibleEmployeeTypes.includes(selectedEmployeeType as EmployeeType);
        
        return matchesSearch && matchesEmployeeType;
    }).sort((a, b) => {
        return a.display.toLowerCase().localeCompare(b.display.toLowerCase());
    }) || [];

    const employeeTypeOptions = [
        {
            displayName: "All",
            value: "All",
        },
        ...Object.keys(EmployeeType).map((employeeType) => {
            return {
                displayName: EMPLOYEE_TYPE_DISPLAY[employeeType as EmployeeType],
                value: employeeType,
            }
        })
    ];

    return (
        <div className="app-custom-properties">
            {customPropertyId === "new" &&
                <AppCustomPropertyNew />
            }
            {customPropertyId !== "new" && customProperties &&
                <>
                    <div className="app-custom-properties-contents">
                        <div className="app-custom-properties-actions">
                            <div className="app-custom-properties-left">
                                <div className="app-custom-properties-search">
                                    <input
                                        type="text"
                                        placeholder="Search by name or label..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="app-custom-properties-search-input"
                                    />
                                </div>
                                <div className="app-custom-properties-filter">
                                    <GenericSelect
                                        options={employeeTypeOptions}
                                        selected={employeeTypeOptions.find(option => option.value === selectedEmployeeType)}
                                        onChange={(value) => setSelectedEmployeeType(value as string)}
                                    />

                                    {/* <select
                                        value={selectedEmployeeType}
                                        onChange={(e) => setSelectedEmployeeType(e.target.value)}
                                        className="app-custom-properties-employee-type-filter"
                                    >
                                        <option value="All">All Employee Types</option>
                                        {Object.keys(EmployeeType).map((employeeType) => (
                                            <option key={employeeType} value={employeeType}>
                                                {EMPLOYEE_TYPE_DISPLAY[employeeType as EmployeeType]}
                                            </option>
                                        ))}
                                    </select> */}
                                </div>
                            </div>
                            <div className="app-custom-properties-right">
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        navigate("/app/custom-properties/new");
                                    }}>
                                    Add
                                </Button>
                            </div>
                            
                            {/* <button
                                className="button-save"
                                onClick={() => {
                                    navigate("/app/custom-properties/new");
                                }}
                            >
                                Add
                            </button> */}
                        </div>
                        <div className="ds2-table-container">
                            <table className="ds2-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Type</th>
                                        <th>Required</th>
                                        <th>Hidden</th>
                                        <th>Roles</th>
                                        <th>Description</th>
                                        <th>Label</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCustomProperties.map((cp) => {
                                        return (
                                            <tr key={cp.id}>
                                                <td>
                                                    <div 
                                                        className="app-custom-properties-grid-display"
                                                        onClick={()=> {
                                                            navigate(`/app/custom-properties/update/${cp.id}`);
                                                        }}
                                                    >
                                                        {cp.display}
                                                    </div>
                                                </td>
                                                <td>
                                                    <MyTooltip title={SETTINGS_DISPLAY[cp.type]}>
                                                        <div>
                                                            {SETTINGS_COMPONENTS[cp.type]}
                                                        </div>
                                                    </MyTooltip>
                                                </td>
                                                <td>
                                                    {cp.required ? (
                                                        <Check size={16} className="required-check-icon" />
                                                    ) : null}
                                                </td>
                                                <td>
                                                    {cp.hidden ? (
                                                        <X size={16} className="hidden-x-icon" />
                                                    ) : null}
                                                </td>
                                                <td>
                                                    <div className="role-icons-container">
                                                        {cp.visibleRoles.map((role, index) => {
                                                            return (
                                                                <MyTooltip key={role} title={ROLE_DISPLAY[role]}>
                                                                    <span className="role-icon-wrapper">
                                                                        {ROLE_ICONS[role]}
                                                                    </span>
                                                                </MyTooltip>
                                                            )
                                                        })}
                                                    </div>
                                                </td>
                                                <td>
                                                    {cp.description}
                                                </td>
                                                <td>
                                                    <div className="app-custom-properties-label">
                                                        {cp.label}
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div style={{paddingBottom: "24px"}}>{ " "}</div>
                    </div>
                </>
            }
        </div>
    )
}